import { useState, useCallback } from 'react';
import { rolesAPI } from '../services/roles';
import { useApi } from '../../../hooks/api/useApi';

export function useRoles() {
  const [roles, setRoles] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  const fetchDepartmentRoles = useCallback(async () => {
    const result = await execute(() => rolesAPI.getDepartmentRoles());
    if (result.success) {
      setRoles(result.data.roles || []);
    }
    return result;
  }, [execute]);

  const fetchRoleById = useCallback(async (roleId) => {
    const result = await execute(() => rolesAPI.getRoleById(roleId));
    if (result.success) {
      setCurrentRole(result.data);
    }
    return result;
  }, [execute]);

  const createRole = useCallback(async (data) => {
    const result = await execute(() => rolesAPI.createRole(data));
    if (result.success) {
      await fetchDepartmentRoles();
    }
    return result;
  }, [execute, fetchDepartmentRoles]);

  const updateRole = useCallback(async (roleId, data) => {
    const result = await execute(() => rolesAPI.updateRole(roleId, data));
    if (result.success) {
      setRoles(prev => prev.map(r => 
        r.role_id === roleId ? { ...r, ...result.data } : r
      ));
      if (currentRole?.role_id === roleId) {
        setCurrentRole(prev => ({ ...prev, ...result.data }));
      }
    }
    return result;
  }, [execute, currentRole]);

  const deleteRole = useCallback(async (roleId) => {
    const result = await execute(() => rolesAPI.deleteRole(roleId));
    if (result.success) {
      setRoles(prev => prev.filter(r => r.role_id !== roleId));
      if (currentRole?.role_id === roleId) {
        setCurrentRole(null);
      }
    }
    return result;
  }, [execute, currentRole]);

  const getRolePermissions = useCallback(async (roleId) => {
    return await execute(() => rolesAPI.getRolePermissions(roleId));
  }, [execute]);

  const addPermissionToRole = useCallback(async (roleId, permissionId) => {
    return await execute(() => rolesAPI.addPermission(roleId, { permission_id: permissionId }));
  }, [execute]);

  const removePermissionFromRole = useCallback(async (roleId, permissionId) => {
    return await execute(() => rolesAPI.removePermission(roleId, permissionId));
  }, [execute]);

  // New bulk operations
  const bulkAddPermissionsToRole = useCallback(async (roleId, permissionIds) => {
    return await execute(() => rolesAPI.bulkAddPermissions(roleId, { permission_ids: permissionIds }));
  }, [execute]);

  const bulkRemovePermissionsFromRole = useCallback(async (roleId, permissionIds) => {
    return await execute(() => rolesAPI.bulkRemovePermissions(roleId, { permission_ids: permissionIds }));
  }, [execute]);

  return {
    roles,
    currentRole,
    loading,
    error,
    clearError,
    fetchDepartmentRoles,
    fetchRoleById,
    createRole,
    updateRole,
    deleteRole,
    getRolePermissions,
    addPermissionToRole,
    removePermissionFromRole,
    bulkAddPermissionsToRole,
    bulkRemovePermissionsFromRole,
  };
}